import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DeviceCounter, WizardChrome, WizardFooter } from '../../components/wizard/WizardChrome';
import { Button } from '../../components/ui/Button';
import { SearchInput } from '../../components/ui/SearchInput';
import { Checkbox, Table, TD, TH, THead, TR } from '../../components/ui/Table';
import { EmptyState } from '../../components/ui/EmptyState';
import { claims } from '../../data/claims';
import { MAX_SHIPMENT_DEVICES, returnAddresses, shipmentByUuid } from '../../data/shipments';
import { useToast } from '../../contexts/ToastContext';
const steps = ['Select Approved Devices', 'Return Shipping Address'];
const approvedDevices = claims.filter(claim => claim.status === 'Approved for Same Unit Repair').map(claim => ({
  id: claim.id,
  makeModel: claim.affectedDevice,
  serialNumber: claim.serialNumber,
  filingDate: claim.filingDate,
  school: claim.schoolName,
  assetTagId: claim.assetTagId,
  studentName: claim.studentName
}));
export function CreateShipment() {
  const {
    uuid
  } = useParams();
  const navigate = useNavigate();
  const {
    showToast
  } = useToast();
  const existing = shipmentByUuid(uuid);
  const editMode = Boolean(existing);
  const [step, setStep] = useState(1);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [addressId, setAddressId] = useState(existing?.addressId ?? returnAddresses[0].id);
  const addedCount = editMode ? existing!.addedDevices : selected.length;
  const complete = addedCount > 0;
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return approvedDevices;
    return approvedDevices.filter(device => `${device.makeModel} ${device.serialNumber} ${device.assetTagId} ${device.studentName}`.toLowerCase().includes(q));
  }, [query]);
  function toggle(id: string) {
    setSelected(prev => {
      if (prev.includes(id)) return prev.filter(s => s !== id);
      if (prev.length >= MAX_SHIPMENT_DEVICES) return prev;
      return [...prev, id];
    });
  }
  function submit() {
    navigate('/shipments');
    showToast(editMode ? 'Shipment Updated' : 'Shipment Created', editMode ? 'Your shipment has been updated successfully' : 'Your shipment has been created successfully');
  }
  return <>
      <WizardChrome title={editMode ? existing!.id : 'Create Shipment'} steps={steps} currentStep={step} onClose={() => navigate('/shipments')} data-id="element-758" data-mp="11roun461mq11" />

      <div className="flex-1 bg-white px-6 py-5" data-id="element-759" data-mp="s0khm9zxhf8u">
        {step === 1 ? <>
            <h2 className="text-[15px] font-bold text-ink-900" data-id="element-760" data-mp="wyvr2jmb9nqj">Select Approved Devices</h2>
            <p className="mt-1 text-[13px] text-ink-500" data-id="element-761" data-mp="1koq1yvn2l285">
              Please select the approved devices to add to the shipment
            </p>

            <div className="mt-4" data-id="element-762" data-mp="1piawgdpgfe2l">
              <SearchInput value={query} onChange={setQuery} placeholder="Search devices by IMEI/SN or asset tag ID" className="w-full max-w-sm" data-id="element-763" data-mp="3a63fneqx5g3k" />
            </div>

            <div className="mt-4" data-id="element-764" data-mp="3iyf9lal8rloa">
              {rows.length === 0 ? <div className="rounded border border-ink-200 bg-white shadow-card" data-id="element-765" data-mp="3eywke2me0ofn">
                  <EmptyState message="No Devices Found" data-id="element-766" data-mp="2r929vrpjjbyk" />
                </div> : <Table minWidth={1000} data-id="element-767" data-mp="2m9zgx5mu4w6y">
                  <THead data-id="element-768" data-mp="2mb0tjeji1qqs">
                    <tr data-id="element-769" data-mp="308h0cr5pjmj2">
                      <TH width={44} data-id="element-770" data-mp="308z223b52syd" />
                      <TH data-id="element-771" data-mp="30a0dw9zzbjh5">Device Make &amp; Model</TH>
                      <TH data-id="element-772" data-mp="2v9cpzx71rir5">Serial Number</TH>
                      <TH data-id="element-773" data-mp="2v8b5z381zlxa">Filing Date</TH>
                      <TH data-id="element-774" data-mp="fhymy0g78hjb">School</TH>
                      <TH data-id="element-775" data-mp="fgwzewhzy2kp">Asset Tag ID</TH>
                      <TH data-id="element-776" data-mp="ajeynte4utns">Student</TH>
                    </tr>
                  </THead>
                  <tbody data-id="element-777" data-mp="alreo10byty7">
                    {rows.map(device => {
                const checked = selected.includes(device.id);
                return <TR key={device.id} selected={checked} onClick={() => toggle(device.id)} data-id="element-778" data-mp="1at6hoeel185k">
                          <TD data-id="element-779" data-mp="1fq6h46gxfcnp">
                            <Checkbox checked={checked} onChange={() => toggle(device.id)} label={`Select ${device.serialNumber}`} data-id="element-780" data-mp="1kqufxebecxsh" />
                          </TD>
                          <TD data-id="element-781" data-mp="1tpfvd0lknwp1">{device.makeModel}</TD>
                          <TD data-id="element-782" data-mp="1yhz8jwgckptb">{device.serialNumber}</TD>
                          <TD data-id="element-783" data-mp="1yklexsri4z9y">{device.filingDate}</TD>
                          <TD data-id="element-784" data-mp="23gakqdu47cep">{device.school}</TD>
                          <TD data-id="element-785" data-mp="23j6jg4dse547">{device.assetTagId}</TD>
                          <TD data-id="element-786" data-mp="28hq1vut68yrt">{device.studentName}</TD>
                        </TR>;
              })}
                  </tbody>
                </Table>}
            </div>
          </> : <>
            <h2 className="text-[15px] font-bold text-ink-900" data-id="element-787" data-mp="3syh6c18nsltu">Select Return Shipping Address</h2>
            <p className="mt-1 text-[13px] text-ink-500" data-id="element-788" data-mp="3nzcx5alqio3i">
              Select the address where the repaired device should be returned
            </p>

            <div className="mt-4" data-id="element-789" data-mp="3j1l3ab1j4rwo">
              <Table minWidth={1000} data-id="element-790" data-mp="3j2mjetxek4l9">
                <THead data-id="element-791" data-mp="kixgzdb3znk2">
                  <tr data-id="element-792" data-mp="ki4h8wakhybf">
                    <TH width={44} data-id="element-793" data-mp="kkqm2o62pzpy" />
                    <TH data-id="element-794" data-mp="fj1btaqodq2x">Return Location</TH>
                    <TH data-id="element-795" data-mp="fk2vu4po5mws">Addressee</TH>
                    <TH data-id="element-796" data-mp="asunzd3zm9lq">Phone</TH>
                    <TH data-id="element-797" data-mp="art1mchiu0op">Email</TH>
                    <TH data-id="element-798" data-mp="ar05e5g6utkt">Address</TH>
                  </tr>
                </THead>
                <tbody data-id="element-799" data-mp="5sygv4zzu21y">
                  {returnAddresses.map(address => {
                const checked = address.id === addressId;
                return <TR key={address.id} selected={checked} onClick={() => setAddressId(address.id)} data-id="element-800" data-mp="yi5g6679uvo9">
                        <TD data-id="element-801" data-mp="2src115ieqz92">
                          <Checkbox checked={checked} onChange={() => setAddressId(address.id)} label={`Select ${address.location}`} data-id="element-802" data-mp="2sr2lkbuzoahm" />
                        </TD>
                        <TD data-id="element-803" data-mp="2nyixgkyodwyk">{address.location}</TD>
                        <TD data-id="element-804" data-mp="2nx7u2m956r7z">{address.addressee}</TD>
                        <TD data-id="element-805" data-mp="291if382djecr">{address.phone ?? ''}</TD>
                        <TD data-id="element-806" data-mp="2e1d75exvy9ze">{address.email}</TD>
                        <TD data-id="element-807" data-mp="2e262ljre9d28">{address.address}</TD>
                      </TR>;
              })}
                </tbody>
              </Table>
            </div>
          </>}
      </div>

      <WizardFooter counter={step === 1 ? <DeviceCounter count={addedCount} max={MAX_SHIPMENT_DEVICES} complete={complete} data-id="element-809" data-mp="3ufd4ci5cjliy" /> : undefined} data-id="element-808" data-mp="25187xs2uihwb">
        {step === 2 && <Button variant="secondary" onClick={() => setStep(1)} data-id="element-810" data-mp="36z7tmgezzfi">
            Back
          </Button>}
        {step === 1 ? <Button disabled={!complete} onClick={() => setStep(2)} data-id="element-811" data-mp="8eyp0rp2jjrz">
            {editMode ? 'Update Shipment' : 'Proceed to the Next Step'}
          </Button> : <Button onClick={submit} data-id="element-812" data-mp="hevj58u89auf">{editMode ? 'Update Shipment' : 'Create Shipment'}</Button>}
      </WizardFooter>
    </>;
}